import { Controller } from "@hotwired/stimulus"

const DRAWER_ID = "booking-task-drawer"
const CONTACT_FIELD = "task[contact_person_ids][]"
// Turbo Frame Contacts::Companies::ContactPreviewModalComponent self-wraps in —
// the same one pipeline--participant-picker opens the booking's contacts in.
const PREVIEW_FRAME_ID = "contact_preview_modal"

// Connects to data-controller="pipeline--booking-task-create"
//
// Glue for the booking form's inline "New task" drawer — the 1:1 Hotwire
// replacement of the React booking-manager's TaskDrawer (CONEK-4230, CONEK-7690).
// Mounted on a wrapper around BOTH the booking <form> and the task drawer
// (rendered as a sibling of the form). Three jobs:
//
//   * load     — when the drawer opens, point its `fields` Turbo Frame at the
//                server-rendered task form (BookingsController#task_form_fields)
//                with the booking's LIVE context: its Title + end date (React
//                defaultTaskName / defaultExpectedClosedDate) and the selected
//                external contacts (booking[contact_ids][], written by
//                pipeline--participant-picker). The server turns the contacts
//                into React's prefills — their Site as default + recommendations,
//                the contacts themselves, the Site owner as assignee.
//   * contacts — React submitted EVERY selected participant as a task contact and
//                its picker replaced the whole list; the extra participants ride as
//                hidden `extraContact` inputs, dropped once the rep picks another
//                contact.
//   * preview  — the drawer's Contact picker rows carry an eye (React's contact
//                rows); its `ui--entity-selector:preview` loads
//                ContactsController#preview into a frame on <body>, so the
//                preview paints above the drawer and the booking modal.
//   * created  — when the drawer form submits (turbo:submit-end), read the new
//                task from the response headers, select it in the linked-task
//                EntitySelector, and close the drawer.
//
// The create endpoint replies 204 + `X-Created-Task-Id` / `X-Created-Task-Name`
// (URL-encoded) headers rather than a body, so Turbo has nothing to render and
// the page never navigates.
export default class extends Controller {
  static targets = ["fields", "extraContact"]

  connect() {
    this._onDrawerOpen = this._onDrawerOpen.bind(this)
    this._onChange = this._onChange.bind(this)
    this._onPreview = this._onPreview.bind(this)
    this._onPreviewEscape = this._onPreviewEscape.bind(this)
    this._dropPreviewFrame = this._dropPreviewFrame.bind(this)
    this.element.addEventListener("drawer:open", this._onDrawerOpen)
    this.element.addEventListener("change", this._onChange)
    this.element.addEventListener(
      "ui--entity-selector:preview",
      this._onPreview,
    )
    window.addEventListener("keydown", this._onPreviewEscape, true)
    document.addEventListener("turbo:before-cache", this._dropPreviewFrame)
  }

  disconnect() {
    this.element.removeEventListener("drawer:open", this._onDrawerOpen)
    this.element.removeEventListener("change", this._onChange)
    this.element.removeEventListener(
      "ui--entity-selector:preview",
      this._onPreview,
    )
    window.removeEventListener("keydown", this._onPreviewEscape, true)
    document.removeEventListener("turbo:before-cache", this._dropPreviewFrame)
    this._dropPreviewFrame()
  }

  _onDrawerOpen(event) {
    if (event.target?.id === DRAWER_ID) this.load()
  }

  // (Re)load the drawer body for the booking's current state. A fresh URL makes
  // Turbo fetch; an unchanged one would be a no-op, so reload() it explicitly —
  // every open starts from a clean form, like React resetting on `open`.
  load() {
    if (!this.hasFieldsTarget) return
    const frame = this.fieldsTarget
    const base = frame.dataset.fieldsUrl
    if (!base) return

    const url = new URL(base, window.location.origin)
    const title = this._formValue('input[name="booking[title]"]')
    if (title) url.searchParams.set("name", title)
    // booking[end_time] is a datetime-local (YYYY-MM-DDTHH:MM); a task due date
    // is a date — take the date part.
    const end = this._formValue('input[name="booking[end_time]"]')
    if (end) url.searchParams.set("due_date", end.slice(0, 10))
    this.element
      .querySelectorAll('input[name="booking[contact_ids][]"]')
      .forEach((input) => {
        if (input.value) url.searchParams.append("contact_ids[]", input.value)
      })

    const src = url.pathname + url.search
    if (
      frame.getAttribute("src") === src &&
      typeof frame.reload === "function"
    ) {
      frame.reload()
    } else {
      frame.setAttribute("src", src)
    }
  }

  // Picking a different contact replaces the participant list (React's
  // handleContactChange set contact_person_ids to [value]).
  _onChange(event) {
    const target = event.target
    if (!target || target.name !== CONTACT_FIELD) return
    if (this.extraContactTargets.includes(target)) return
    this.extraContactTargets.forEach((input) => input.remove())
  }

  // Select the created task in the linked-task field and close the drawer.
  created(event) {
    if (event.detail?.success !== true) return
    // Clear any error a previous failed submit streamed into the drawer.
    document.getElementById("booking-task-drawer-errors")?.replaceChildren()

    const headers = event.detail.fetchResponse?.response?.headers
    const id = headers?.get("X-Created-Task-Id")
    if (!id) return
    const name = decodeURIComponent(headers.get("X-Created-Task-Name") || "")

    const selectorEl = this.element.querySelector(
      '[data-testid="booking-form-task-field"] [data-controller~="ui--entity-selector"]',
    )
    const selector =
      selectorEl &&
      this.application.getControllerForElementAndIdentifier(
        selectorEl,
        "ui--entity-selector",
      )
    if (selector) selector._commitSelection(id, name || id)

    const drawer = document.getElementById(DRAWER_ID)
    const drawerController =
      drawer &&
      this.application.getControllerForElementAndIdentifier(
        drawer,
        "ui--drawer",
      )
    drawerController?.close?.()
  }

  // Open the contact preview for a Contact row's eye. The event is not
  // cancelled, so the selector closes its list and gives focus back.
  _onPreview(event) {
    const src = event.detail?.url
    if (!src) return
    let frame = document.getElementById(PREVIEW_FRAME_ID)
    if (!frame) {
      frame = document.createElement("turbo-frame")
      frame.id = PREVIEW_FRAME_ID
      document.body.appendChild(frame)
      this._previewFrame = frame
    }
    if (
      frame.getAttribute("src") === src &&
      typeof frame.reload === "function"
    ) {
      frame.reload()
    } else {
      frame.setAttribute("src", src)
    }
  }

  // Every open Ui::ModalComponent / drawer closes on Escape, so one Escape over
  // the preview would also dismiss the task drawer and the booking modal behind
  // it. While the preview is open, take Escape first and close only it.
  _onPreviewEscape(event) {
    if (event.key !== "Escape") return
    const dialog = document
      .getElementById(PREVIEW_FRAME_ID)
      ?.querySelector("dialog[open]")
    if (!dialog) return
    event.preventDefault()
    event.stopImmediatePropagation()
    const modal = this.application.getControllerForElementAndIdentifier(
      dialog,
      "ui--modal",
    )
    if (modal) modal.close()
    else dialog.close()
  }

  // The frame lives on <body>, outside this element, and a cached snapshot must
  // not carry it (CONEK-6282): drop the one this controller created.
  _dropPreviewFrame() {
    this._previewFrame?.remove()
    this._previewFrame = null
  }

  _formValue(selector) {
    const input = this.element.querySelector(selector)
    return input ? input.value : ""
  }
}
